// @ts-check

/**
 * @file Renders the current user profile route.
 */

import {useLocalSearchParams} from "expo-router"
import {launchImageLibraryAsync} from "expo-image-picker"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo, useEffect} from "react"
import {Platform} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"
import Button from "../src/components/button"
import TextInput from "../src/components/text-input"
import Card from "../src/components/card"
import {User} from "../src/frontend-models/user"
import {useScreenLoading} from "../src/components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../src/shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"
import ScreenTitle from "../src/components/screen-title"
import {userFromProfileCommandResponse} from "../src/auth/use-current-user"
import {attachUserProfilePicture} from "../src/profile-pictures/profile-picture-source"
import errorMessage from "../src/shared/error-message"
import UserAvatar from "../src/components/user-avatar"

/** @import {ChangeEvent, NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{email?: string | null, first_name?: string | null, last_name?: string | null, name?: string | null, id: string | number}} ProfileUser */
/** @typedef {{email: string, firstName: string, lastName: string, password: string, passwordConfirmation: string}} ProfileForm */
/** @typedef {{contentBase64: string, contentType: string, filename: string}} ProfilePictureUpload */
/** @typedef {{kind: "error" | "success", text: string} | null} ProfileStatus */

/**
 * Builds editable form values from a profile user.
 * @param {ProfileUser} user Profile user response.
 * @returns {ProfileForm} Editable profile form.
 */
function formFromUser(user) {
  return {
    email: user.email || "",
    firstName: user.first_name || "",
    lastName: user.last_name || "",
    password: "",
    passwordConfirmation: ""
  }
}

/** @typedef {{[key: string]: unknown}} ProfileIndexScreenProps */

/**
 * @typedef {object} ProfileIndexScreenState
 * @property {ProfileForm} form Editable profile form values.
 * @property {boolean} loading Whether the profile is loading.
 * @property {ProfilePictureUpload | null} selectedProfilePicture Newly selected picture pending upload.
 * @property {boolean} saving Whether the profile is saving.
 * @property {ProfileStatus} status Screen status message.
 * @property {string | null} userId Current user identifier.
 */

/**
 * Profile route migrated from the legacy Rails/React app.
 * @augments {ShapeComponent<ProfileIndexScreenProps, ProfileIndexScreenState>}
 */
class ProfileIndexScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** @type {ProfileIndexScreenState} */
  state = {
    form: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      passwordConfirmation: ""
    },
    loading: true,
    selectedProfilePicture: null,
    saving: false,
    status: null,
    userId: null
  }
  loadRequestId = 0

  /** Initializes route params, palette, and profile loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()

    this.params = params
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadProfile()
    }, [])
  }

  /**
   * Renders the profile edit form.
   * @returns {ReactNode} Profile screen content.
   */
  render() {
    const {form, loading, saving, selectedProfilePicture, status, userId} = this.s
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="profileScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="profileScreen"
      >
        <ScreenTitle>{_("Your profile")}</ScreenTitle>
        <Text
          style={styles[`title-${this.tt.themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="profileScreen/title"
        >
          {_("Your profile")}
        </Text>
        <Card
          style={styles.formCardSpacing ||= {
            gap: 12,
            maxWidth: 520,
            width: "100%"
          }}
          testID="profileForm"
        >
          {userId &&
            <View
              style={styles.profilePictureField ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 16
              }}
              testID="profileForm/profilePictureField"
            >
              <UserAvatar
                displayName={[form.firstName, form.lastName].filter(Boolean).join(" ") || form.email || String(userId)}
                size={80}
                source={selectedProfilePicture
                  ? `data:${selectedProfilePicture.contentType};base64,${selectedProfilePicture.contentBase64}`
                  : undefined}
                testID="profileForm/profilePicture"
                userId={userId}
              />
              <View
                style={styles.profilePictureControls ||= {
                  flex: 1,
                  gap: 8
                }}
                testID="profileForm/profilePictureControls"
              >
                <Text
                  style={styles[`label-${this.tt.themeName}`] ||= {
                    color: this.tt.colors.textMuted,
                    fontWeight: 600
                  }}
                  testID="profileForm/profilePictureLabel"
                >
                  {_("Profile picture")}
                </Text>
                {Platform.OS === "web" &&
                  <input
                    accept="image/*"
                    data-testid="profileForm/profilePictureInput"
                    onChange={this.tt.onProfilePictureInputChange}
                    type="file"
                  />
                }
                {Platform.OS !== "web" &&
                  <Button
                    iconName="image"
                    label={_("Choose profile picture")}
                    onPress={this.tt.onChooseProfilePicturePress}
                    testID="profileForm/profilePictureButton"
                    variant="neutral"
                  />
                }
                {selectedProfilePicture &&
                  <Text
                    style={styles[`profilePictureFilename-${this.tt.themeName}`] ||= {
                      color: this.tt.colors.textMuted,
                      fontSize: 13
                    }}
                    testID="profileForm/profilePictureFilename"
                  >
                    {selectedProfilePicture.filename}
                  </Text>
                }
              </View>
            </View>
          }
          <Text
            style={styles[`label-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontWeight: 600
            }}
            testID="profileForm/emailLabel"
          >
            {_("Email")}
          </Text>
          <TextInput
            autoCapitalize="none"
            keyboardType="email-address"
            onChangeText={this.tt.onEmailChangeText}
            style={styles.inputSpacing ||= {fontSize: 16}}
            testID="profileForm/emailInput"
            value={form.email}
          />
          <Text style={styles[`label-${this.tt.themeName}`]} testID="profileForm/firstNameLabel">
            {_("First name")}
          </Text>
          <TextInput
            onChangeText={this.tt.onFirstNameChangeText}
            style={styles.inputSpacing}
            testID="profileForm/firstNameInput"
            value={form.firstName}
          />
          <Text style={styles[`label-${this.tt.themeName}`]} testID="profileForm/lastNameLabel">
            {_("Last name")}
          </Text>
          <TextInput
            onChangeText={this.tt.onLastNameChangeText}
            style={styles.inputSpacing}
            testID="profileForm/lastNameInput"
            value={form.lastName}
          />
          <Text style={styles[`label-${this.tt.themeName}`]} testID="profileForm/passwordLabel">
            {_("Password")}
          </Text>
          <TextInput
            onChangeText={this.tt.onPasswordChangeText}
            secureTextEntry
            style={styles.inputSpacing}
            testID="profileForm/passwordInput"
            value={form.password}
          />
          <Text
            style={styles[`label-${this.tt.themeName}`]}
            testID="profileForm/passwordConfirmationLabel"
          >
            {_("Password confirmation")}
          </Text>
          <TextInput
            onChangeText={this.tt.onPasswordConfirmationChangeText}
            secureTextEntry
            style={styles.inputSpacing}
            testID="profileForm/passwordConfirmationInput"
            value={form.passwordConfirmation}
          />
          {status &&
            <Text
              style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                fontWeight: 600
              }}
              testID="profileForm/statusMessage"
            >
              {status.text}
            </Text>
          }
          <Button
            disabled={saving}
            iconName="floppy-disk"
            label={saving ? _("Saving...") : _("Save")}
            onPress={this.tt.onSavePress}
            testID="profileForm/saveButton"
          />
        </Card>
        <Card
          style={styles.authTokensCardSpacing ||= {
            gap: 12,
            marginTop: 16,
            maxWidth: 520,
            width: "100%"
          }}
          testID="profileScreen/authTokensCard"
        >
          <Text
            style={styles[`authTokensTitle-${this.tt.themeName}`] ||= {
              color: this.tt.colors.text,
              fontSize: 18,
              fontWeight: 700
            }}
            testID="profileScreen/authTokensCard/title"
          >
            {_("Auth tokens")}
          </Text>
          <Text
            style={styles[`authTokensDescription-${this.tt.themeName}`] ||= {
              color: this.tt.colors.textMuted,
              fontSize: 14
            }}
            testID="profileScreen/authTokensCard/description"
          >
            {_("Generate tokens an AI bot or app can use to act on your behalf through the API.")}
          </Text>
          <Button
            href="/auth-tokens"
            iconName="key"
            label={_("Manage auth tokens")}
            testID="profileScreen/authTokensCard/manageButton"
            variant="neutral"
          />
        </Card>
      </View>
    )
  }

  /**
   * Loads the current user's profile.
   * @returns {Promise<void>} Resolves when profile loading completes.
   */
  loadProfile = async () => {
    const requestId = ++this.loadRequestId
    try {
      const response = await User.currentProfile()
      if (this.loadRequestId !== requestId) return
      const user = userFromProfileCommandResponse(response)
      if (!user) {
        throw new Error("Profile response did not include a user")
      }
      this.s.form = formFromUser(user)
      this.s.userId = String(user.id)
      this.s.loading = false
    } catch (error) {
      if (this.loadRequestId !== requestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: `${_("Could not load profile")}: ${error instanceof Error ? error.message : String(error)}`
      }
    }
  }

  /**
   * Applies one profile form value.
   * @param {keyof ProfileForm} key Profile form key to update.
   * @param {string} value Text to store in the form.
   * @returns {void} Nothing.
   */
  updateFormValue(key, value) {
    this.s.form = {
      ...this.s.form,
      [key]: value
    }
    this.s.status = null
  }

  /**
   * Applies the email field.
   * @param {string} value Address text from the input.
   * @returns {void} Nothing.
   */
  onEmailChangeText = (value) => {
    this.tt.updateFormValue("email", value)
  }

  /**
   * Applies the first name field.
   * @param {string} value Given-name text from the input.
   * @returns {void} Nothing.
   */
  onFirstNameChangeText = (value) => {
    this.tt.updateFormValue("firstName", value)
  }

  /**
   * Applies the last name field.
   * @param {string} value Family-name text from the input.
   * @returns {void} Nothing.
   */
  onLastNameChangeText = (value) => {
    this.tt.updateFormValue("lastName", value)
  }

  /**
   * Applies the password field.
   * @param {string} value Secret text from the input.
   * @returns {void} Nothing.
   */
  onPasswordChangeText = (value) => {
    this.tt.updateFormValue("password", value)
  }

  /**
   * Applies the password confirmation field.
   * @param {string} value Repeated secret text from the input.
   * @returns {void} Nothing.
   */
  onPasswordConfirmationChangeText = (value) => {
    this.tt.updateFormValue("passwordConfirmation", value)
  }

  /**
   * Stores a selected browser image as an attachment upload.
   * @param {ChangeEvent<HTMLInputElement>} event Browser input change.
   * @returns {void} Nothing.
   */
  onProfilePictureInputChange = (event) => {
    const file = event.target.files?.[0]
    if (!file) {
      this.s.selectedProfilePicture = null
      return
    }
    if (!file.type.startsWith("image/")) {
      this.s.status = {kind: "error", text: _("Please choose an image file")}
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : ""
      const contentBase64 = dataUrl.includes(",") ? dataUrl.split(",", 2)[1] : ""
      if (!contentBase64) {
        this.s.status = {kind: "error", text: _("Could not read the selected image")}
        return
      }
      this.s.selectedProfilePicture = {
        contentBase64,
        contentType: file.type,
        filename: file.name
      }
      this.s.status = null
    }
    reader.onerror = () => {
      this.s.status = {
        kind: "error",
        text: errorMessage(reader.error || new Error("Could not read the selected image"))
      }
    }
    reader.readAsDataURL(file)
  }

  /**
   * Opens the native image library and stores the selected picture.
   * @returns {Promise<void>} Resolves after selection handling.
   */
  onChooseProfilePicturePress = async () => {
    try {
      const result = await launchImageLibraryAsync({
        allowsEditing: true,
        aspect: [1, 1],
        base64: true,
        mediaTypes: ["images"],
        quality: 0.85
      })
      if (result.canceled) return

      const asset = result.assets[0]
      if (!asset?.base64) throw new Error(_("Could not read the selected image"))
      this.s.selectedProfilePicture = {
        contentBase64: asset.base64,
        contentType: asset.mimeType || "image/jpeg",
        filename: asset.fileName || "profile-picture.jpg"
      }
      this.s.status = null
    } catch (error) {
      this.s.status = {kind: "error", text: errorMessage(error)}
    }
  }

  /**
   * Saves the current profile form.
   * @returns {Promise<void>} Resolves when save handling completes.
   */
  onSavePress = async () => {
    const {form, selectedProfilePicture, userId} = this.s
    if (
      (form.password || form.passwordConfirmation) &&
      form.password !== form.passwordConfirmation
    ) {
      this.s.status = {
        kind: "error",
        text: _("Passwords do not match")
      }
      return
    }
    this.s.saving = true
    this.s.status = null
    try {
      const response = await User.updateProfile({
        email: form.email,
        first_name: form.firstName,
        last_name: form.lastName,
        password: form.password
      })
      const user = userFromProfileCommandResponse(response)
      if (!user) {
        throw new Error("Profile update response did not include a user")
      }
      const updatedUserId = String(user.id || userId || "")
      if (!updatedUserId) throw new Error("Profile update response did not include a user id")
      if (selectedProfilePicture) {
        await attachUserProfilePicture({upload: selectedProfilePicture, userId: updatedUserId})
      }
      this.s.form = formFromUser(user)
      this.s.selectedProfilePicture = null
      this.s.userId = updatedUserId
      this.s.status = {
        kind: "success",
        text: _("Your profile was saved")
      }
    } catch (error) {
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    } finally {
      this.s.saving = false
    }
  }
}

/** @type {NamedExoticComponent<ProfileIndexScreenProps>} */
const ProfileIndexScreenComponent = memo(shapeComponent(ProfileIndexScreen))

export default ProfileIndexScreenComponent
